import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Globe, Plus, X, Play, Loader2, Send,
  Ban, Trash2, Bot, AlertTriangle, CheckCircle2, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sevColor = { critical: "text-red-400 bg-red-500/10", high: "text-orange-400 bg-orange-500/10", medium: "text-amber-400 bg-amber-500/10" };

function ScoreRing({ score }) {
  const color = score >= 85 ? "text-emerald-400" : score >= 60 ? "text-amber-400" : "text-red-400";
  return <span className={`font-mono text-2xl font-bold ${color}`} data-testid="autopilot-score">{score}<span className="text-xs text-zinc-500">/100</span></span>;
}

export default function SecurityAutoPilot() {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [reports, setReports] = useState([]);
  const [config, setConfig] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [blockIp, setBlockIp] = useState("");
  const [tgToken, setTgToken] = useState("");
  const [tgChat, setTgChat] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [d, r, c, b, u] = await Promise.allSettled([
      axios.get(`${API}/security/autopilot/domains`),
      axios.get(`${API}/security/autopilot/reports`),
      axios.get(`${API}/security/autopilot/config`),
      axios.get(`${API}/security/cf-blocks`),
      axios.get(`${API}/security/autopilot/uptime`),
    ]);
    if (d.status === "fulfilled") setDomains(d.value.data);
    if (r.status === "fulfilled") setReports(r.value.data);
    if (c.status === "fulfilled") { setConfig(c.value.data); setTgChat(c.value.data.telegram_chat_id || ""); }
    if (b.status === "fulfilled") setBlocks(b.value.data);
    if (u.status === "fulfilled") setUptime(Object.fromEntries(u.value.data.map(s => [s.domain, s])));
  };
  useEffect(() => { load(); const iv = setInterval(() =>
    axios.get(`${API}/security/autopilot/uptime`).then(r =>
      setUptime(Object.fromEntries(r.data.map(s => [s.domain, s])))).catch(() => {}), 60000);
    return () => clearInterval(iv);
  }, []);

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await axios.post(`${API}/security/autopilot/domains`, { domain: newDomain });
      setNewDomain(""); toast.success("Domain added to daily checks"); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Add failed"); }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await axios.post(`${API}/security/autopilot/run`);
      toast.success(`Check complete — score ${data.overall_score}/100`);
      load();
    } catch (e) { if (e.message !== "Step-up cancelled") toast.error("Check failed"); }
    finally { setBusy(""); }
  };

  const saveConfig = async (extra = {}) => {
    const body = { telegram_chat_id: tgChat, ...extra };
    if (tgToken) body.telegram_token = tgToken;
    const { data } = await axios.put(`${API}/security/autopilot/config`, body);
    setConfig(data); setTgToken("");
    toast.success("Settings saved");
  };

  const testTelegram = async () => {
    setBusy("tg");
    try { await axios.post(`${API}/security/autopilot/telegram-test`); toast.success("Test message sent — check Telegram"); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Test failed"); }
    finally { setBusy(""); }
  };

  const doBlock = async () => {
    setBusy("block");
    try {
      await axios.post(`${API}/security/cf-blocks`, { ip: blockIp, note: "manual block from AutoPilot page" });
      toast.success(`${blockIp} blocked at Cloudflare edge`); setBlockIp(""); load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Block failed"); }
    finally { setBusy(""); }
  };

  const doUnblock = async (rule_id, ip) => {
    if (!window.confirm(`Unblock ${ip} at Cloudflare?`)) return;
    try { await axios.delete(`${API}/security/cf-blocks/${rule_id}`); toast.success("Unblocked"); load(); }
    catch (e) { if (e.message !== "Step-up cancelled") toast.error("Unblock failed"); }
  };

  const latest = reports[0];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="security-autopilot-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-500" /> Security AutoPilot
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Daily AI check of all your apps & domains at 07:00 IST, report to Telegram</p>
        </div>
        <Button onClick={runNow} disabled={busy === "run"} className="bg-blue-600 hover:bg-blue-700" data-testid="autopilot-run-btn">
          {busy === "run" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />} Run check now
        </Button>
      </div>

      {/* Domains */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" /> Monitored domains ({domains.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {domains.map(d => (
              <Badge key={d.domain} variant="outline" className="border-white/10 text-zinc-200 gap-1.5 py-1" data-testid={`domain-chip-${d.domain}`}>
                {d.domain}
                <button onClick={() => axios.delete(`${API}/security/autopilot/domains/${d.domain}`).then(load)} className="hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {domains.length === 0 && <p className="text-sm text-zinc-500">No domains yet — add your apps below.</p>}
          </div>
          <div className="flex gap-2">
            <Input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="erp.stancor.in"
                   onKeyDown={e => e.key === "Enter" && addDomain()}
                   className="bg-black/50 border-white/10 text-white" data-testid="add-domain-input" />
            <Button onClick={addDomain} variant="outline" className="border-white/10 text-zinc-200 hover:bg-white/5" data-testid="add-domain-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Latest report */}
      {latest && (
        <Card className="bg-[#0c0c0e] border-white/5" data-testid="latest-report-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-400" /> Latest report — {new Date(latest.created_at).toLocaleString()}
              </CardTitle>
              <CardDescription>{latest.domains_checked} domains · {latest.critical_count} critical findings · trigger: {latest.trigger}</CardDescription>
            </div>
            <ScoreRing score={latest.overall_score} />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 flex gap-2" data-testid="ai-summary-block">
              <Bot className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-200 whitespace-pre-line">{latest.ai_summary}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {latest.results.map(r => (
                <div key={r.domain} className="bg-black/40 rounded-lg p-3 border border-white/5" data-testid={`report-domain-${r.domain}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono text-sm text-white">{r.domain}</span>
                    <span className={`font-mono text-sm ${r.score >= 85 ? "text-emerald-400" : r.score >= 60 ? "text-amber-400" : "text-red-400"}`}>{r.score}/100</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mb-2">HTTP {r.status_code ?? "—"} · TLS expires in {r.tls_days_left ?? "?"} days</p>
                  {r.findings.length === 0 ? (
                    <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> All clear</p>
                  ) : r.findings.map((f, i) => (
                    <p key={i} className={`text-xs rounded px-2 py-1 mb-1 flex items-center gap-1.5 ${sevColor[f.severity] || "text-zinc-400 bg-white/5"}`}>
                      <AlertTriangle className="w-3 h-3 shrink-0" /> {f.detail}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cloudflare blocks */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><Ban className="w-4 h-4 text-red-400" /> Cloudflare Firewall Blocks</CardTitle>
          <CardDescription>Blocks apply at the Cloudflare edge — attackers never reach your servers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between bg-black/40 rounded-lg p-3 border border-white/5">
            <div>
              <p className="text-sm text-zinc-200">Auto-block attacker IPs</p>
              <p className="text-[11px] text-zinc-500">When repeated failed logins lock an account, push that IP to Cloudflare automatically</p>
            </div>
            <Switch checked={config?.auto_cf_block || false}
                    onCheckedChange={v => saveConfig({ auto_cf_block: v })}
                    data-testid="auto-block-toggle" />
          </div>
          <div className="flex gap-2">
            <Input value={blockIp} onChange={e => setBlockIp(e.target.value)} placeholder="Block an IP e.g. 203.0.113.5"
                   className="bg-black/50 border-white/10 text-white font-mono" data-testid="block-ip-input" />
            <Button onClick={doBlock} disabled={busy === "block" || !blockIp} className="bg-red-600 hover:bg-red-700" data-testid="block-ip-btn">
              {busy === "block" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Block"}
            </Button>
          </div>
          {blocks.length > 0 && (
            <div className="space-y-1.5" data-testid="cf-blocks-list">
              {blocks.map(b => (
                <div key={b.rule_id} className="flex items-center justify-between bg-black/40 rounded px-3 py-2 border border-white/5">
                  <div>
                    <span className="font-mono text-sm text-red-300">{b.ip}</span>
                    <span className="text-[11px] text-zinc-500 ml-2">{b.note}</span>
                  </div>
                  <button onClick={() => doUnblock(b.rule_id, b.ip)} className="text-zinc-500 hover:text-red-400" data-testid={`unblock-${b.ip}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Telegram */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><Send className="w-4 h-4 text-sky-400" /> Telegram morning report</CardTitle>
          <CardDescription>Daily report at 07:00 IST {config?.telegram_token_set ? "— channel configured ✓" : "— add your bot token & chat ID"}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between bg-black/40 rounded-lg p-3 border border-white/5">
            <div>
              <p className="text-sm text-zinc-200">Instant uptime alerts</p>
              <p className="text-[11px] text-zinc-500">Probe every domain each minute — Telegram ping within ~2 min of an outage, plus a recovery ping</p>
            </div>
            <Switch checked={config?.uptime_alerts ?? true}
                    onCheckedChange={v => saveConfig({ uptime_alerts: v })}
                    data-testid="uptime-alerts-toggle" />
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Input value={tgToken} onChange={e => setTgToken(e.target.value)} type="password"
                   placeholder={config?.telegram_token_set ? "Bot token (saved — enter to replace)" : "Bot token from @BotFather"}
                   className="bg-black/50 border-white/10 text-white" data-testid="tg-token-input" />
            <Input value={tgChat} onChange={e => setTgChat(e.target.value)} placeholder="Chat ID"
                   className="bg-black/50 border-white/10 text-white font-mono" data-testid="tg-chat-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => saveConfig()} variant="outline" className="border-white/10 text-zinc-200 hover:bg-white/5" data-testid="tg-save-btn">Save</Button>
            <Button onClick={testTelegram} disabled={busy === "tg"} className="bg-sky-600 hover:bg-sky-700" data-testid="tg-test-btn">
              {busy === "tg" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send test message"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
